import { useEffect, useId, useRef, useState } from "react";
import styled from "styled-components";
import Button from "../Button/Button";
import Typography from "../Typography";
import PropTypes from "prop-types";
import CloseIcon from "../Icons/CloseIcon";
import { theme } from "../../theme";

const BASE_MODAL_Z_INDEX = 1050;
const MODAL_STACK_STEP = 20;
const OPEN_MODAL_IDS = [];
let nextModalId = 0;
let bodyScrollLockState = null;

const applyBodyScrollLock = () => {
	if (bodyScrollLockState || typeof document === "undefined") {
		return;
	}

	const scrollY = window.scrollY;
	bodyScrollLockState = {
		overflow: document.body.style.overflow,
		position: document.body.style.position,
		top: document.body.style.top,
		width: document.body.style.width,
		scrollY,
	};

	document.body.style.overflow = "hidden";
	document.body.style.position = "fixed";
	document.body.style.top = `-${scrollY}px`;
	document.body.style.width = "100%";
};

const releaseBodyScrollLock = () => {
	if (!bodyScrollLockState || typeof document === "undefined") {
		return;
	}

	const { overflow, position, top, width, scrollY } = bodyScrollLockState;
	document.body.style.overflow = overflow;
	document.body.style.position = position;
	document.body.style.top = top;
	document.body.style.width = width;
	bodyScrollLockState = null;

	if (process.env.NODE_ENV !== "test") {
		window.scrollTo(0, scrollY);
	}
};

const isTopmostModal = (modalId) =>
	OPEN_MODAL_IDS[OPEN_MODAL_IDS.length - 1] === modalId;

const Modal = ({
	children,
	onClose,
	title,
	isOpen = true, // Nueva prop para controlar visibilidad
	closeOnOverlayClick = true,
	maxWidth = "500px",
	maxHeight,
	height,
	minHeight,
	// overflowY = 'auto'
	overflowY,
	desktopAlignment = "center", // Nueva prop para alineación en desktop
	mobileAlignment = "center", // Nueva prop para alineación en móvil
	fullScreen = false,
	fullScreenOnMobile = false,
	closeButtonPosition = "end",
	hideHeader = false,
	closeLabel = "Close",
}) => {
	const modalIdRef = useRef(null);
	const closeButtonRef = useRef(null);
	const modalContentRef = useRef(null);
	const previousActiveElementRef = useRef(null);
	const onCloseRef = useRef(onClose);
	const titleId = useId();
	const [stackIndex, setStackIndex] = useState(0);
	const shouldUseFullScreenOnMobile = fullScreen || fullScreenOnMobile;

	useEffect(() => {
		onCloseRef.current = onClose;
	}, [onClose]);

	useEffect(() => {
		if (!isOpen) {
			return undefined;
		}

		const modalId = ++nextModalId;
		modalIdRef.current = modalId;
		previousActiveElementRef.current = document.activeElement;
		OPEN_MODAL_IDS.push(modalId);
		setStackIndex(OPEN_MODAL_IDS.length - 1);
		applyBodyScrollLock();

		const focusTimer = window.setTimeout(() => {
			const activeElement = document.activeElement;
			const hasFocusedElementInsideModal =
				modalContentRef.current &&
				activeElement &&
				activeElement !== document.body &&
				modalContentRef.current.contains(activeElement);

			if (!hasFocusedElementInsideModal) {
				if (closeButtonRef.current) {
					closeButtonRef.current.focus();
				} else {
					modalContentRef.current?.focus();
				}
			}
		}, 0);

		const handleKeyDown = (event) => {
			if (event.key !== "Escape" || !isTopmostModal(modalId)) {
				return;
			}

			event.stopPropagation();
			onCloseRef.current?.();
		};

		document.addEventListener("keydown", handleKeyDown);

		return () => {
			window.clearTimeout(focusTimer);
			document.removeEventListener("keydown", handleKeyDown);
			const modalIndex = OPEN_MODAL_IDS.indexOf(modalId);
			if (modalIndex !== -1) {
				OPEN_MODAL_IDS.splice(modalIndex, 1);
			}

			if (OPEN_MODAL_IDS.length === 0) {
				releaseBodyScrollLock();
			}

			if (
				previousActiveElementRef.current &&
				typeof previousActiveElementRef.current.focus === "function"
			) {
				previousActiveElementRef.current.focus();
			}
		};
	}, [isOpen]);

	return (
		<>
			{isOpen && (
				<ModalWrapper
					$desktopAlignment={desktopAlignment}
					$mobileAlignment={mobileAlignment}
					$fullScreen={fullScreen}
					$fullScreenOnMobile={shouldUseFullScreenOnMobile}
					$stackIndex={stackIndex}
					onClick={(e) => {
						if (closeOnOverlayClick && e.target === e.currentTarget) {
							onClose();
						}
					}}
				>
					<ModalContent
						ref={modalContentRef}
						tabIndex={-1}
						$fullScreen={fullScreen}
						$fullScreenOnMobile={shouldUseFullScreenOnMobile}
						$stackIndex={stackIndex}
						$closeButtonPosition={closeButtonPosition}
						role="dialog"
						aria-modal="true"
						aria-labelledby={titleId}
						style={{
							maxWidth: fullScreen ? "100%" : maxWidth,
							maxHeight: fullScreen ? "100%" : maxHeight,
							height: fullScreen ? "100%" : height,
							minHeight: fullScreen ? "100%" : minHeight,
							overflowY: overflowY,
						}}
					>
						{hideHeader ? (
							<VisuallyHiddenTitle id={titleId}>{title}</VisuallyHiddenTitle>
						) : (
							<ModalHeader $closeButtonPosition={closeButtonPosition}>
								<Button
									ref={closeButtonRef}
									$variant="ghost"
									$iconOnly
									onClick={onClose}
									aria-label={closeLabel}
								>
									<CloseIcon
										fill={theme.colors.defaultMain}
										width={20}
										height={20}
									/>
								</Button>
								<Typography
									id={titleId}
									$variant="title-5-semibold"
									$variantTablet="body-1-semibold"
									$variantMobile="body-2-semibold"
									as="h2"
									color={theme.colors.defaultStrong}
								>
									{title}
								</Typography>
							</ModalHeader>
						)}
						{children}
					</ModalContent>
				</ModalWrapper>
			)}
		</>
	);
};

Modal.propTypes = {
	children: PropTypes.node.isRequired,
	onClose: PropTypes.func.isRequired,
	title: PropTypes.string.isRequired,
	isOpen: PropTypes.bool, // Nueva prop para controlar visibilidad
	closeOnOverlayClick: PropTypes.bool,
	maxWidth: PropTypes.string,
	maxHeight: PropTypes.string,
	height: PropTypes.string,
	minHeight: PropTypes.string,
	overflowY: PropTypes.string,
	desktopAlignment: PropTypes.oneOf(["flex-start", "center", "flex-end"]), // Validación para alineación en desktop
	mobileAlignment: PropTypes.oneOf(["flex-start", "center", "flex-end"]), // Validación para alineación en móvil
	fullScreen: PropTypes.bool,
	fullScreenOnMobile: PropTypes.bool,
	hideHeader: PropTypes.bool,
	closeLabel: PropTypes.string,
	closeButtonPosition: PropTypes.oneOf(["start", "end"]),
};

export default Modal;

const ModalWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  height: 100dvh; /* Usar dynamic viewport height para móviles */
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: ${({ $desktopAlignment }) => $desktopAlignment || "center"};
  background: ${({ theme }) => `${theme.fill.inverseMain}80`};
  backdrop-filter: blur(12px);
  z-index: ${({ $stackIndex = 0 }) => BASE_MODAL_Z_INDEX + $stackIndex * MODAL_STACK_STEP};
  padding: ${({ theme, $fullScreen }) =>
		$fullScreen ? theme.sizing.none || "0px" : theme.sizing.sm};
  overflow-x: hidden;
  overflow-y: auto;

  @media (max-height: 720px) and (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    align-items: flex-start;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme, $fullScreenOnMobile }) =>
			$fullScreenOnMobile ? theme.sizing.none || "0px" : theme.sizing.xs};
    padding-bottom: ${({ theme, $fullScreenOnMobile }) =>
			$fullScreenOnMobile
				? theme.sizing.none || "0px"
				: `calc(${theme.sizing.xs} + env(safe-area-inset-bottom))`};
    align-items: ${({ $mobileAlignment }) => $mobileAlignment || "center"};
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background-color: ${({ theme }) => theme.fill.defaultMain};
  border-radius: ${({ theme, $fullScreen }) =>
		$fullScreen ? theme.radius.none || "0px" : theme.radius.sm};
  z-index: ${({ $stackIndex = 0 }) => BASE_MODAL_Z_INDEX + $stackIndex * MODAL_STACK_STEP + 1};
  width: 100%;
  max-height: 100%;
  overflow-y: auto;

  ${({ $fullScreen }) =>
		$fullScreen &&
		`
    height: 100%;
    min-height: 100%;
  `}

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    max-height: ${({ theme, $fullScreenOnMobile }) =>
			$fullScreenOnMobile
				? "100dvh"
				: `calc(100dvh - ${theme.sizing.xs} * 2 - env(safe-area-inset-bottom))`};
    height: ${({ $fullScreenOnMobile }) =>
			$fullScreenOnMobile ? "100dvh" : "auto"};
    min-height: ${({ $fullScreenOnMobile }) =>
			$fullScreenOnMobile ? "100dvh" : "auto"};
    border-radius: ${({ theme, $fullScreenOnMobile }) =>
			$fullScreenOnMobile ? theme.radius.none || "0px" : theme.radius.sm};
  }
`;

const ModalHeader = styled.div`
  display: flex;
  flex-direction: ${({ $closeButtonPosition }) =>
		$closeButtonPosition === "start" ? "row" : "row-reverse"};
  padding: ${({ theme }) => theme.sizing.xs} ${({ theme }) => theme.sizing.xs} ${({ theme }) => theme.sizing.xs} ${({ theme }) => theme.sizing.sm};
  justify-content: flex-start;
  align-items: center;
  gap: ${({ theme }) => theme.sizing.xs};
  align-self: stretch;
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  background: ${({ theme }) => theme.fill.defaultMain};

  h2 {
    flex: 1;
    text-align: left;
  }
`;

const VisuallyHiddenTitle = styled.h2`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;
